import { Effect, Option, Predicate } from 'effect';

import { REFRESH_LOCK_NAME } from '@vs-shop/engine/config/tokenRefresh';

import { AuthTokenRefreshError } from '@vs-shop/engine/data/error/auth';
import { shouldRefreshToken } from './shouldRefreshToken';
import { refreshCustomerToken } from '../../bind/customer';
import { AuthToken } from '../../service/auth/AuthToken';
import { RefreshLock } from '@vs-shop/engine/service/auth/RefreshLock';
import { StorageSync } from '../../service/auth/StorageSync';

export const refreshTokenFlow = Effect.gen(function* () {
  const refreshLock = yield* RefreshLock;
  const authTokenService = yield* AuthToken;
  const storageSync = yield* StorageSync;

  return yield* refreshLock.tryLock(
    REFRESH_LOCK_NAME,
    Effect.gen(function* () {
      const needsRefresh = yield* shouldRefreshToken;

      if (!needsRefresh) {
        yield* Effect.logDebug('Token already up-to-date, skipping refresh');
        return;
      }

      const refreshTokenOption = yield* authTokenService.getRefreshToken.pipe(
        Effect.orElseSucceed(() => Option.none<string>())
      );

      if (Option.isNone(refreshTokenOption)) {
        return;
      }

      const response = yield* refreshCustomerToken(refreshTokenOption.value).pipe(
        Effect.mapError(
          (error) =>
            new AuthTokenRefreshError({
              message: `Token refresh failed: ${error.message}`,
              cause: error,
            })
        )
      );

      if (
        !response.token ||
        !response.refresh_token ||
        Predicate.isNullish(response.refresh_token_expiration)
      ) {
        return yield* new AuthTokenRefreshError({
          message: 'Token refresh response missing required fields',
        });
      }

      yield* authTokenService.setTokens(
        response.token,
        response.refresh_token,
        response.refresh_token_expiration
      );

      yield* storageSync.broadcastTokensUpdated;

      yield* Effect.logDebug('Token refreshed successfully');
    }).pipe(
      Effect.withLogSpan('refreshTokenFlow'),
      Effect.annotateLogs({ operation: 'token-refresh' })
    )
  );
}).pipe(Effect.withLogSpan('refreshTokenFlow.outer'));
